import type { GoogleAnalyticsEventName, GoogleAnalyticsEventProperties } from './google-analytics.events';
import { getLogger } from '@packages/core/logging';
import {
    GOOGLE_ANALYTICS_LIMITS,
    GOOGLE_ANALYTICS_NAME_PATTERN,
    GOOGLE_ANALYTICS_PARAMS,
    GOOGLE_ANALYTICS_PAGE_PARAM_VALUE_LIMITS,
    GOOGLE_ANALYTICS_RESERVED_PARAM_PREFIXES,
    truncate,
    isAllowedParameterName,
    type GoogleAnalyticsParamName,
} from './google-analytics.params';

const logger = getLogger('google-analytics');

/**
 * Build a GA4 event payload from the event name and properties. Returns 'null' if the event name
 * is invalid, which will cause the event to be dropped.
 *
 * @param eventName The name of the GA4 event.
 * @param properties The properties of the GA4 event.
 * @returns The GA4 event payload, or null if the event name is invalid.
 */
export function buildEventPayload(
    eventName: GoogleAnalyticsEventName,
    properties: GoogleAnalyticsEventProperties | undefined
): Record<string, unknown> | null {
    if (
        !eventName ||
        eventName.length > GOOGLE_ANALYTICS_LIMITS.EVENT_NAME_MAX_LENGTH ||
        !GOOGLE_ANALYTICS_NAME_PATTERN.test(eventName)
    ) {
        logger.warn(`[google-analytics] event name invalid, dropped: ${eventName}`);
        return null;
    }

    const payload: Record<string, unknown> = { event: eventName };
    if (!properties) {
        return payload;
    }

    let count = 0;
    for (const [name, value] of Object.entries(properties)) {
        if (count >= GOOGLE_ANALYTICS_LIMITS.PARAMETERS_PER_EVENT_MAX) {
            logger.warn(
                `[google-analytics] event "${eventName}" exceeded ${GOOGLE_ANALYTICS_LIMITS.PARAMETERS_PER_EVENT_MAX} params, dropped: ${name}`
            );
            continue;
        }

        if (!isValidParameterName(name)) {
            logger.warn(`[google-analytics] event "${eventName}" param name invalid, dropped: ${name}`);
            continue;
        }

        if (!isAllowedParameterName(name)) {
            logger.warn(`[google-analytics] event "${eventName}" param name not allowed, dropped: ${name}`);
            continue;
        }

        const sanitized = sanitizeParameterValue(name, value);
        if (sanitized === null || sanitized === undefined) {
            continue;
        }

        payload[name] = sanitized;
        count += 1;
    }
    return payload;
}

/**
 * Checks a parameter name against GA4's structural rules: it must match the GA4 name pattern, stay within the
 * parameter name length cap, and avoid GA4's reserved prefixes.
 *
 * This is the shape check that runs before the allow-list check in {@link buildEventPayload}. The two are separate
 * concerns: a name can be perfectly well-formed and still not be a parameter we have opted into. Reserved prefixes
 * matter most here, since GA4 accepts those names at collection time and then discards them without any error,
 * making the loss invisible unless we reject them ourselves.
 *
 * @param name - the parameter name to check
 * @returns true when the name is structurally valid for GA4
 */
function isValidParameterName(name: string): boolean {
    if (name.length > GOOGLE_ANALYTICS_LIMITS.PARAMETER_NAME_MAX_LENGTH || !GOOGLE_ANALYTICS_NAME_PATTERN.test(name)) {
        return false;
    }
    return !GOOGLE_ANALYTICS_RESERVED_PARAM_PREFIXES.some((prefix) => name.startsWith(prefix));
}

/**
 * Returns 'null' to indicate the value should be dropped, including an empty string.
 * Boolean and number values are returned as-is. Strings are transformed and length capped.
 *
 * @param paramName The name of the GA4 parameter.
 * @param value The value of the GA4 parameter.
 * @returns The sanitized value, or null if it should be dropped.
 */
function sanitizeParameterValue(paramName: GoogleAnalyticsParamName, value: unknown): unknown {
    if (value == null) {
        return null;
    }

    if (typeof value !== 'string') {
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
        return null;
    }

    const trimmed = value.trim();

    if (trimmed === '') {
        return null;
    }

    const transformed = GOOGLE_ANALYTICS_PARAMS[paramName](trimmed);

    if (transformed === '') {
        return null;
    }

    return truncate(
        transformed,
        GOOGLE_ANALYTICS_PAGE_PARAM_VALUE_LIMITS[paramName] ?? GOOGLE_ANALYTICS_LIMITS.PARAMETER_VALUE_MAX_LENGTH
    );
}
